import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import MemberCancelReasonTable from '../../../components/tables/MemberCancelReasonTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useMemberCancelReasonSearch, useDeleteMemberCancelReason } from '../../../hooks/useMemberCancelReasonApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { MemberCancelReasonDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const MemberCancelReasonList: React.FC = () => {
  const { search } = useMemberCancelReasonSearch();
  const deleteMutation = useDeleteMemberCancelReason();
  const [items, setItems] = useState<MemberCancelReasonDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Member Cancel Reasons', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      setIsLoading(true);
      const { results, totalCount } = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(results);
      setTotalRecords(totalCount);
    } catch (error) {
      console.error('Error fetching Member Cancel Reasons:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: MemberCancelReasonDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting MemberCancelReason:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/membercancelreasons"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.MemberCancelReason.View"
    >
      <Card
        title="Manage Member Cancel Reasons"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton
            onClick={() => navigate('/admin/membercancelreasons/create')}
          />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="title"
              label="Search by Title"
              value={searchFilters.title || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, title: e.target.value })
              }
              placeholder="Search by Title"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <MemberCancelReasonTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/membercancelreasons/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default MemberCancelReasonList;
